import React, { useState, useMemo, useCallback } from 'react';
import Modal from 'pages/components/Modal/Modal';
import CustomButton from 'pages/components/CustomButton/CustomButton';
import HeroSection from '../components/HeroSection';
import ControllerFeatures from '../components/ControllerFeatures';
import DownloadComponent from '../components/DownloadComponent';
import QuoteRequestForm from 'pages/OurDrones/QuoteRequest/QuoteRequestForm';
import DroneSpecifications from 'pages/OurDrones/DroneSpecification/DroneSpecification';
import BVLOSOperations from 'pages/OurDrones/BVLOSOperations/BVLOSOperations';
import Certifications from 'pages/OurDrones/Certifications/Certifications';
import { useApi } from 'hooks/useApi';
import styles from './AxlOneView.module.css';

const CMS_BASE_URL = process.env.REACT_APP_CMS_BASE_URL;

const AxlOneView = () => {
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const fallbackImages = useMemo(() => ({
    controllerMain: '/Assets/Images/axlOneView/droneOne.svg',
    controllerSide: '/Assets/Images/axlOneView/droneTwo.svg',
    controllerHand: '/Assets/Images/axlOneView/droneThree.svg',
    controllerMiddle: '/Assets/Images/axlOneView/droneFour.svg',
    controllerBottom: '/Assets/Images/axlOneView/droneFive.svg'
  }), []);

  const { data: productData, loading: productLoading, error: productError } = useApi('/product-types', { params: { slug: 'axl-oneview' } });
  const { data: specificationsResponse, loading: specsLoading, error: specsError } = useApi('/products', { params: { slug: 'axl-oneview' } });

  const specificationsData = useMemo(() => {
    if (!specificationsResponse?.success || !specificationsResponse?.products) return [];
    return specificationsResponse.products.map(product => ({
      ...product,
      specifications: product.specification?.map(spec => ({ label: spec.name, value: spec.value })) || []
    }));
  }, [specificationsResponse]);

  const getFeaturesData = useMemo(() => {
    if (!productData?.success || !productData?.products?.features) return [];
    return productData.products.features.map(item => ({ icon: item.icon, label: item.title }));
  }, [productData]);

  const getBVLOSOperationsData = useMemo(() => {
    if (!productData?.success || !productData?.products?.designed_for) return [];
    return productData.products.designed_for.map(item => ({ icon: item.icon, label: item.title, image: item.image }));
  }, [productData]);

  const getAdditionalImages = useMemo(() => {
    if (!productData?.success) {
      return [
        { src: fallbackImages.controllerHand, alt: 'Controller Hand View' },
        { src: fallbackImages.controllerMiddle, alt: 'Controller Middle View' },
        { src: fallbackImages.controllerBottom, alt: 'Controller Bottom View'}
      ];
    }

    const images = [];
    const product = productData.products;
    if (product?.image1) images.push({ src: product.image1, alt: 'Controller View 1' });
    if (product?.image2) images.push({ src: product.image2, alt: 'Controller View 2' });
    if (product?.image3) images.push({ src: product.image3, alt: 'Controller View 3' });
    
    return images.length > 0 ? images : [
      { src: fallbackImages.controllerHand, alt: 'Controller Hand View' },
      { src: fallbackImages.controllerMiddle, alt: 'Controller Middle View' },
      { src: fallbackImages.controllerBottom, alt: 'Controller Bottom View'}
    ];
  }, [productData, fallbackImages]);

  const getStoreData = useMemo(() => {
    if (!productData?.success || !productData?.products?.store) return [];
    return productData.products.store;
  }, [productData]);

  const product = useMemo(() => productData?.success ? productData.products : null, [productData]);

  const handleOpenQuoteModal = useCallback(() => setIsQuoteModalOpen(true), []);
  const handleCloseQuoteModal = useCallback(() => setIsQuoteModalOpen(false), []);

  const loading = productLoading || specsLoading;
  const error = productError || specsError;

  if (loading) return (
    <div className={styles.pageWrapper}>
      <div className={styles.loadingContainer}>
        <p>Loading...</p>
      </div>
    </div>
  );

  if (error) return (
    <div className={styles.pageWrapper}>
      <div className={styles.errorContainer}>
        <p>Error: {error}</p>
      </div>
    </div>
  );

  return (
    <div className={styles.pageWrapper}>
      {/* Hero Section */}
      <section className={styles.heroSection}>
        <HeroSection
          subtitle={product?.title || "axl One View"}
          title={product?.banner_heading || "Low Latency, Long Range, Mission-Ready"}
          gradientFrom="#F97738"
          gradientTo="#FCB999"
          showBottomFade={false}
        />
      </section>

      {/* Controller Overlap Section */}
      <div className={styles.controllerWrapper}>
        <img
          id="controllerImage"
          src={product?.banner_image ? `${CMS_BASE_URL}/attachments/${product.banner_image}` : fallbackImages.controllerMain}
          alt="AXL OneView Controller"
          className={styles.controllerImage}
          width="640"
          height="400"
          loading="eager"
          fetchpriority="high"
        />
      </div>

      {/* Main Content */}
      <div className={styles.mainContainer}>
        {/* Description */}
        <section className={styles.descriptionSection}>
          <h2>
            {product?.description || 
              <>
                axl OneView - India's First WPC Certified Remote Controller, is the latest, most advanced Remote Controller available in the market with an impressive range of 15 km of real-time video transmission and control in high-definition with low latency. Its advanced processor and integration with the custom-developed Drone Operating System (axlDOS) ensure clear images and extended operational range, even in challenging environments. The system supports seamless but encrypted data transmission via multiple interfaces, including HDMI, WLAN & USB.
                <br /><br />
                axl OneView offers advanced mesh networking capabilities, enabling multiple units to interconnect, creating a dynamic, Secure extended network with the real-time broadcast of the video over the internet that enhances coverage and range for large-scale operations. This feature is ideal for complex missions where multiple drones or units must collaborate across vast distances.
              </>
            }
          </h2>
        </section>

        {/* Controller Features */}
        {getFeaturesData.length > 0 && (
          <ControllerFeatures
            mainImage={product?.image1 ? `${CMS_BASE_URL}/attachments/${product.image1}` : fallbackImages.controllerSide}
            mainImageAlt="Controller Side View"
            additionalImages={getAdditionalImages}
            features={getFeaturesData}
          />
        )}

        {/* Quote Button */}
        <section className={styles.quoteSection}>
          <CustomButton
            onClick={handleOpenQuoteModal}
            size="large"
            ariaLabel="Request a Quote"
          >
            Request a Quote
          </CustomButton>
        </section>

        {/* Quote Modal */}
        <Modal isOpen={isQuoteModalOpen} onClose={handleCloseQuoteModal} title="Request a Quote" maxWidth="40rem">
          <QuoteRequestForm onSuccess={handleCloseQuoteModal} productSlug={product?.slug} />
        </Modal>

        {/* Certifications with Security Text */}
        <div className={styles.certificationsWrapper}>
          <Certifications description={false}/>
          <div className={styles.securityContainer}>
            <p>
              Engineered to the highest standards of security and safety, AXL OneView features digitally 
              signed firmware and advanced encryption protocols—ensuring secure, reliable, and 
              efficient operation in every mission.
            </p>
          </div>
        </div>

        {/* Full Width Sections */}
        {getBVLOSOperationsData.length > 0 && (
          <BVLOSOperations 
            operations={getBVLOSOperationsData} 
            title={product?.designed_for_title || 'Designed For BVLOS Operations'}
          />
        )}

        {/* Specifications and Downloads */}
        <div className={styles.specificationWrapper}>
          {specificationsData.map((specProduct, index) => (
            <React.Fragment key={specProduct.slug || index}>
              <DroneSpecifications 
                droneImage={specProduct.image ? `${CMS_BASE_URL}/attachments/${specProduct.image}` : fallbackImages.controllerMain}
                specifications={specProduct.specifications || []}
                title={specProduct.title}
                description={specProduct.description}
              />
              
              <DownloadComponent 
                brochure={specProduct.brochure ? `${CMS_BASE_URL}/attachments/${specProduct.brochure}` : null}
              />
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AxlOneView;